// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { useEffect, useState } from "react"
import {
  IonButton,
  IonButtons,
  IonFooter,
  IonGrid,
  IonItem,
  IonLabel,
  IonList,
  IonRow,
  IonText,
  IonToolbar
} from "@ionic/react"
import { useLocales } from "react-localized"
import ReactMarkdown from 'react-markdown'
import { BasePurchaseOrderPageProps } from "../components"
import { ScreenSizeConditional, SectionContainer } from "../lib"
import { ResponsiveIonCol } from "../components/ResponsiveIonCol"
import ProcurementSectionMenu from "./ProcurementSectionMenu"


const PurchaseOrderReview: React.FC<
  BasePurchaseOrderPageProps
  & {onPrevious(): void
}> = ({
  onPrevious,
  onSubmit,
  order,
}) => {
  const {gettext} = useLocales()
  const [canSubmit, setCanSubmit] = useState<boolean>(false)

  useEffect(() => {
    setCanSubmit(order.isValid())
  }, [order])

  return (
    <>
      <SectionContainer
        canGoBack={false}
        menuComponent={ProcurementSectionMenu}
        ready={true}
        title={gettext("Confirm purchase order")}
      >
        <IonGrid style={{paddingTop: "var(--section-padding)"}}>
          <IonRow className="panel">
            <ResponsiveIonCol>
              <IonText>
                <ReactMarkdown>
                  {gettext("Ensure that the details below are correct and click **Place purchase order** to send the order to the supplier.")}
                </ReactMarkdown>
              </IonText>
              <IonList lines="none">
                <IonItem>
                  <IonLabel>
                    <h2>{gettext("Buyer")}</h2>
                    <p>{order.getBuyerName()}</p>
                  </IonLabel>
                </IonItem>
                <IonItem>
                  <IonLabel>
                    <h2>{gettext("Supplier")}</h2>
                    <p>{order.getSupplierName()}</p>
                  </IonLabel>
                </IonItem>
                <IonItem>
                  <IonLabel>
                    <h2>{gettext("Location")}</h2>
                    <p>{order.getAddressFormatted()}</p>
                  </IonLabel>
                </IonItem>
                <ScreenSizeConditional full>
                  <IonItem>
                    <IonButton
                      color={(!!canSubmit) ? "primary" : "dark"}
                      disabled={!canSubmit}
                      onClick={() => onSubmit()}
                      size="default"
                    >
                      {gettext("Place purchase order")}
                    </IonButton>
                    <IonButton
                      color="medium"
                      disabled={!canSubmit}
                      fill="clear"
                      onClick={() => onPrevious()}
                      size="default"
                    >
                      {gettext("Back")}
                    </IonButton>
                  </IonItem>
                </ScreenSizeConditional>
              </IonList>
            </ResponsiveIonCol>
          </IonRow>
        </IonGrid>
      </SectionContainer>
      <ScreenSizeConditional>
        <IonFooter>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton
                color={(!!canSubmit) ? "primary" : "dark"}
                disabled={!canSubmit}
                fill="clear"
                onClick={() => onSubmit()}
              >
                {gettext("Place purchase order")}
              </IonButton>
            </IonButtons>
            <IonButtons slot="end">
              <IonButton
                color="medium"
                disabled={!canSubmit}
                fill="clear"
                onClick={() => onPrevious()}
              >
                {gettext("Back")}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
      </ScreenSizeConditional>
    </>
  )
}

export default PurchaseOrderReview